import { useEffect, useState } from "react";
import { NavLink, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight, UploadCloud } from "lucide-react";
import OrphionMark from "@/components/OrphionMark";
import { NAV, CV } from "@/constants/testIds";

const NAV_LINKS = [
  { to: "/gcc-support", label: "GCC Support", testId: "nav-link-gcc" },
  { to: "/ai-native-it-services", label: "AI-Native IT", testId: "nav-link-ainative" },
  { to: "/services", label: "Services", testId: NAV.linkServices },
  { to: "/how-we-work", label: "How We Work", testId: "nav-link-howwework" },
  { to: "/case-studies", label: "Case Studies", testId: "nav-link-cases" },
  { to: "/jobs", label: "Jobs", testId: "nav-link-jobs" },
  { to: "/insights", label: "Insights", testId: "nav-link-insights" },
  { to: "/about", label: "About", testId: NAV.linkAbout },
];

export const Header = ({ onOpenCv }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <motion.header
      initial={{ y: -20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.76, 0, 0.24, 1], delay: 0.2 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[rgb(var(--bg-rgb)/0.94)] backdrop-blur-xl border-b border-[var(--line-strong)] shadow-[0_1px_0_rgba(20,17,13,0.06)]"
          : "bg-[rgb(var(--bg-rgb)/0.82)] backdrop-blur-md border-b border-[var(--line)]"
      }`}
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 flex items-center justify-between h-[80px] gap-6">
        <Link to="/" data-testid={NAV.logo} className="flex items-center gap-3 group shrink-0">
          <OrphionMark
            size={26}
            className="text-[var(--accent)] group-hover:text-[var(--accent-hover)] transition-colors"
          />
          <span className="font-display text-xl md:text-2xl tracking-[-0.02em] text-[var(--ink)] leading-none font-medium">
            orphion
            <span className="hidden sm:inline font-serif-accent italic text-[var(--accent)] ml-1.5 tracking-tight">
              global hr
            </span>
          </span>
        </Link>

        <nav className="hidden 2xl:flex items-center gap-6">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              data-testid={l.testId}
              className={({ isActive }) =>
                `link-slide whitespace-nowrap text-[13px] uppercase tracking-[0.22em] font-medium transition-colors ${
                  isActive
                    ? "text-[var(--ink)]"
                    : "text-[var(--ink)]/75 hover:text-[var(--ink)]"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <button
            onClick={onOpenCv}
            data-testid={CV.headerBtn}
            aria-label="Drop CV"
            className="btn-ghost text-xs hover:border-[var(--accent)] hover:text-[var(--accent)] !px-3 xl:!px-6"
          >
            <UploadCloud className="w-4 h-4" strokeWidth={1.7} />
            <span className="sr-only">Drop CV</span>
          </button>
          <Link to="/book-diagnostic" data-testid={NAV.cta} className="btn-pill text-sm">
            <span>Book a Call</span>
            <ArrowUpRight className="w-4 h-4" strokeWidth={1.6} />
          </Link>
        </div>

        <div className="2xl:hidden flex items-center gap-2">
          <button
            onClick={onOpenCv}
            data-testid={`${CV.headerBtn}-mobile`}
            aria-label="Drop CV"
            className="lg:hidden p-2 border border-[var(--line-strong)] rounded-full text-[var(--ink)] hover:text-[var(--accent)] hover:border-[var(--accent)] transition-colors"
          >
            <UploadCloud className="w-4 h-4" strokeWidth={1.7} />
          </button>
          <button
            className="text-[var(--ink)]"
            onClick={() => setOpen((s) => !s)}
            data-testid={NAV.menuToggle}
            aria-label="Toggle menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.35, ease: [0.76, 0, 0.24, 1] }}
            className="2xl:hidden bg-[var(--bg)] border-t border-[var(--line)] px-6 pt-8 pb-12"
          >
            <div className="flex flex-col gap-5">
              {NAV_LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.end}
                  onClick={() => setOpen(false)}
                  data-testid={`${l.testId}-mobile`}
                  className="font-display text-3xl tracking-tight text-[var(--ink)]"
                >
                  {l.label}
                </NavLink>
              ))}
              <button
                onClick={() => {
                  setOpen(false);
                  onOpenCv?.();
                }}
                className="btn-ghost w-fit mt-2"
              >
                <UploadCloud className="w-4 h-4" />
                <span>Drop CV</span>
              </button>
              <Link
                to="/book-diagnostic"
                onClick={() => setOpen(false)}
                className="btn-pill w-fit mt-2"
                data-testid={`${NAV.cta}-mobile`}
              >
                <span>Book a Call</span>
                <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Header;
